import {
  Children,
  ComponentProps,
  JSX,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
} from "react";

import { cn } from "@oneleet/utils";

import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";

const DEFAULT_VARIANT = "outlined";

const inputContainer = cva(
  "flex h-min shrink-0 items-center justify-center overflow-hidden rounded-md",
  {
    variants: {
      variant: {
        outlined:
          "shadow-[0px_0px_0px_0.5px_theme(colors.neutral.300),_0px_1px_3px_-1px_rgba(0,_0,_0,_0.05),_0px_1px_3px_0px_rgba(0,_0,_0,_0.10)] transition-shadow duration-100 focus-within:shadow-[0px_0px_0px_1px_theme(colors.accent.800),_0px_0px_0px_4px_theme(colors.accent.200),_0px_1px_3px_-1px_rgba(0,_0,_0,_0.05),_0px_1px_3px_0px_rgba(0,_0,_0,_0.10)] hover:shadow-[0px_0px_0px_1px_theme(colors.neutral.300),_0px_1px_3px_-1px_rgba(0,_0,_0,_0.05),_0px_1px_3px_0px_rgba(0,_0,_0,_0.10)] focus-within:hover:shadow-[0px_0px_0px_1px_theme(colors.accent.800),_0px_0px_0px_4px_theme(colors.accent.200),_0px_1px_3px_-1px_rgba(0,_0,_0,_0.05),_0px_1px_3px_0px_rgba(0,_0,_0,_0.10)] sm:min-w-[200px]",
        plain: "",
      },
      disabled: {
        true: "opacity-50",
        false: "",
      },
    },
    defaultVariants: {
      variant: DEFAULT_VARIANT,
      disabled: false,
    },
  },
);

const input = cva(
  "text-ng-md 3 w-full border-none bg-transparent px-3 text-neutral-700 outline-none placeholder:text-neutral-500 focus:ring-0",
  {
    variants: {
      variant: {
        outlined: "py-1.5",
        plain: "py-0",
      },
      disabled: {
        true: "cursor-not-allowed",
        false: "",
      },
    },
    defaultVariants: {
      variant: DEFAULT_VARIANT,
      disabled: false,
    },
  },
);

const iconClassNames = cva(
  "text-neutral-550 size-4 shrink-0 first:ml-3 last:mr-3",
);

const NgInputContext = createContext<{
  variant?: VariantProps<typeof input>["variant"];
  disabled?: boolean;
  children?: React.ReactNode;
}>({});

const useNgInputContext = () => {
  const context = useContext(NgInputContext);
  if (!context) {
    throw new Error("NgInput compound components must be used within NgInput");
  }
  return context;
};

export type Props = ComponentProps<"input"> & VariantProps<typeof input>;

const NgInput = forwardRef<HTMLDivElement, Props>(
  (
    { variant = DEFAULT_VARIANT, disabled, className, children, ...rest },
    ref,
  ) => {
    // Validate that there is exactly one NgInput.Input child
    const childrenArray = Children.toArray(children);
    const inputChildrenCount = childrenArray.filter(
      (child) =>
        isValidElement(child) &&
        ((child.type as React.ComponentType).displayName === "NgInputInput" ||
          (child.type as React.ComponentType).displayName ===
            "NgDropdownMenuInputInput"),
    ).length;

    if (inputChildrenCount !== 1) {
      throw new Error("NgInput requires exactly one NgInput.Input child.");
    }

    return (
      <NgInputContext.Provider value={{ variant, disabled, children }}>
        <div
          ref={ref}
          className={cn(inputContainer({ variant, disabled }), className)}
          {...rest}
        >
          {children}
        </div>
      </NgInputContext.Provider>
    );
  },
);
NgInput.displayName = "NgInput";

type NgInputComponent = typeof NgInput & {
  //eslint-disable-next-line @typescript-eslint/naming-convention
  Input: typeof NgInputInput;
  //eslint-disable-next-line @typescript-eslint/naming-convention
  Adornment: typeof NgInputAdornment;
  //eslint-disable-next-line @typescript-eslint/naming-convention
  Icon: typeof NgInputIcon;
};

export const NgInputInput = forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input">
>(({ className, ...props }, ref) => {
  const { variant, disabled, children } = useNgInputContext();
  const childrenArray = Children.toArray(children);
  const iconIndex = childrenArray.findIndex(
    (child) =>
      isValidElement(child) &&
      ((child.type as React.ComponentType).displayName === "NgInputIcon" ||
        (child.type as React.ComponentType).displayName ===
          "NgDropdownMenuInputIcon"),
  );
  const isIconFirstChild = iconIndex === 0;
  const isIconLastChild = iconIndex === childrenArray.length - 1;

  return (
    <input
      ref={ref}
      className={cn(
        input({ variant, disabled }),
        isIconFirstChild && "pl-2",
        isIconLastChild && "pr-2",
        className,
      )}
      disabled={disabled}
      {...props}
    />
  );
});
NgInputInput.displayName = "NgInputInput";
(NgInput as NgInputComponent).Input = NgInputInput;

export const NgInputAdornment = forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "text-ng-md bg-neutral-50 px-2 py-1.5 text-neutral-500 outline outline-[0.5px] outline-neutral-300",
        className,
      )}
      {...props}
    />
  );
});
NgInputAdornment.displayName = "NgInputAdornment";
(NgInput as NgInputComponent).Adornment = NgInputAdornment;

export const NgInputIcon = forwardRef<
  HTMLDivElement,
  {
    icon: JSX.Element;
    className?: string;
  }
>(({ icon, className }, ref) => {
  return cloneElement(icon, {
    ref,
    className: cn(iconClassNames(), className),
  });
});
NgInputIcon.displayName = "NgInputIcon";
(NgInput as NgInputComponent).Icon = NgInputIcon;

export default NgInput as NgInputComponent;
